// EnquiryToast — micro-hero notification triggered when a visitor
// clicks "Reservar" on any hotel-card CTA (homepage carousel,
// property-card grid, deep-dive pages, etc.).
//
// UX intent:
//   • Confirm the property is added ("✓ Kasbah Xaluca Arfoud added to
//     your enquiry") without immediately whisking the visitor away
//     from where they are — this is what unlocks the multi-property
//     conversion behaviour Grup Xaluca depends on (kasbah + bivouac +
//     refuge combos vs. single-hotel OTA bookings).
//   • Offer TWO deterministic next steps side-by-side:
//       "Ver reserva →"     → primary CTA, navigates to /booking
//       "Añadir otra"       → secondary, dismisses and keeps the user
//                             browsing more properties.
//   • Fully i18n (EN · FR · ES) via the language-aware `pick()` helper
//     so the message reads natively regardless of locale.
//
// Implementation notes:
//   • Uses sonner's `toast.custom()` for a fully-branded, non-generic
//     surface (rounded corners, brand palette, MapPin location line).
//   • Duration is 6s — long enough for a considered choice, short
//     enough to auto-clean if the user ignores it.
//   • Never navigates programmatically without an explicit click —
//     honours "user is in control".

import { Check, ArrowRight, MapPin, X } from "lucide-react";
import { toast } from "sonner";

const pick = (dict, lang) => dict?.[lang] || dict?.en || "";

const COPY = {
  added: {
    en: "Added to your enquiry",
    fr: "Ajouté à votre demande",
    es: "Añadido a tu solicitud",
  },
  view: {
    en: "View enquiry",
    fr: "Voir la demande",
    es: "Ver reserva",
  },
  add_another: {
    en: "Add another",
    fr: "Ajouter une autre",
    es: "Añadir otra",
  },
  close: { en: "Close", fr: "Fermer", es: "Cerrar" },
};

// Renders the rich toast body. Kept as a pure component so the props
// contract (hotelName, location, onView, onDismiss, lang) is testable
// in isolation.
const EnquiryToastCard = ({
  hotelName,
  location,
  totalItems,
  lang,
  onView,
  onDismiss,
  testIdPrefix,
}) => (
  <div
    className="w-[360px] max-w-[calc(100vw-2rem)] bg-[#1A1513] text-[#FDFBF7] border border-[#D4A373]/40 shadow-[0_18px_44px_-16px_rgba(0,0,0,0.55)] pointer-events-auto"
    data-testid={`${testIdPrefix}-root`}
    role="status"
    aria-live="polite"
  >
    {/* Top row — check icon + property name + close button */}
    <div className="flex items-start gap-3 px-4 pt-4">
      <div
        className="mt-0.5 w-8 h-8 shrink-0 rounded-full bg-[#C16542]/25 border border-[#C16542]/70 flex items-center justify-center"
        aria-hidden="true"
      >
        <Check className="w-4 h-4 text-[#D4A373]" strokeWidth={2.2} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] tracking-[0.28em] uppercase text-[#D4A373]">
          {pick(COPY.added, lang)}
          {totalItems > 1 ? (
            <span className="ml-2 text-[#FDFBF7]/60 tracking-[0.2em]">
              · {totalItems}
            </span>
          ) : null}
        </p>
        <p
          className="mt-1 font-serif-x text-base leading-tight truncate text-[#FDFBF7]"
          data-testid={`${testIdPrefix}-title`}
        >
          {hotelName}
        </p>
        {location ? (
          <p className="mt-0.5 inline-flex items-center gap-1 text-[10px] tracking-[0.22em] uppercase text-[#FDFBF7]/60">
            <MapPin className="w-3 h-3 text-[#D4A373]" strokeWidth={1.6} />
            <span className="truncate">{location}</span>
          </p>
        ) : null}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className="shrink-0 -mt-1 -mr-1 inline-flex items-center justify-center w-8 h-8 text-[#FDFBF7]/60 hover:text-[#FDFBF7] hover:bg-[#FDFBF7]/8 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4A373]"
        aria-label={pick(COPY.close, lang)}
        data-testid={`${testIdPrefix}-close`}
      >
        <X className="w-4 h-4" strokeWidth={1.6} />
      </button>
    </div>

    {/* Action row — twin CTAs, primary + secondary side-by-side */}
    <div className="mt-4 px-4 pb-4 flex items-center gap-2">
      <button
        type="button"
        onClick={onView}
        className="group/toast-view flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] text-[10px] tracking-[0.25em] uppercase transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4A373]"
        data-testid={`${testIdPrefix}-view`}
      >
        {pick(COPY.view, lang)}
        <ArrowRight
          className="w-3 h-3 group-hover/toast-view:translate-x-0.5 transition-transform"
          strokeWidth={1.8}
        />
      </button>
      <button
        type="button"
        onClick={onDismiss}
        className="flex-1 inline-flex items-center justify-center px-3 py-2.5 border border-[#FDFBF7]/40 hover:border-[#D4A373] text-[#FDFBF7]/90 hover:text-[#D4A373] text-[10px] tracking-[0.25em] uppercase transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4A373]"
        data-testid={`${testIdPrefix}-add-another`}
      >
        {pick(COPY.add_another, lang)}
      </button>
    </div>
  </div>
);

/**
 * Trigger the rich Enquiry Toast.
 *
 * @param {object} args
 * @param {string} args.hotelName      Localised display name (e.g. "Kasbah Hotel Xaluca Arfoud").
 * @param {string=} args.location      Optional short location line ("Arfoud · Gateway to the Desert").
 * @param {number}  args.totalItems    Current count of properties in the enquiry (chip on the right of the header).
 * @param {string}  args.lang          Active language ("en" | "fr" | "es").
 * @param {() => void} args.onView     Called when the visitor clicks "Ver reserva".
 * @param {string=} args.testIdPrefix  Test-id base (defaults to "enquiry-toast").
 */
export function showEnquiryToast({
  hotelName,
  location,
  totalItems,
  lang,
  onView,
  testIdPrefix = "enquiry-toast",
}) {
  toast.custom(
    (id) => (
      <EnquiryToastCard
        hotelName={hotelName}
        location={location}
        totalItems={totalItems}
        lang={lang}
        onView={() => {
          toast.dismiss(id);
          onView?.();
        }}
        onDismiss={() => toast.dismiss(id)}
        testIdPrefix={testIdPrefix}
      />
    ),
    { duration: 6000, position: "bottom-center" }
  );
}

export default EnquiryToastCard;
